import React from "react";
import { useSiteBuilder } from "../SiteBuilder";
import {
	Center,
	VStack,
	Spinner,
	Text,
	Box,
	Button,
	ButtonGroup,
	Flex,
	Grid,
	Heading,
	HStack,
	SimpleGrid,
	Image,
	useDisclosure,
	useToast,
	Stack,
} from "@chakra-ui/react";
import { __, sprintf } from "@wordpress/i18n";
import { useMutation, useQuery } from "react-query";
import { localized } from "../../../utils/global";
import { TemplateCard, templateCards } from "./TemplateCards";
import { DownloadIcon, ViewIcon } from "../constants/icons";
import TemplateLists from "./TemplateLists";
import axios from "redaxios";
import apiFetch from "@wordpress/api-fetch";
import { useNavigate, useSearchParams } from "react-router-dom";
import defaultImage from "../../../../images/placeholder.png";

const URL_REGEX =
	/\b((https?|ftp|file):\/\/|(www|ftp)\.)[-A-Z0-9+&@#\/%?=~_|$!:,.;]*[A-Z0-9+&@#\/%=~_|$]/gi;

const saveImagesLocally = async (content = "") => {
	let allUrls = [...content.matchAll(URL_REGEX)];
	const urls = allUrls
		.map((url) => url.at(0) ?? "")
		.filter((url, idx, arr) => arr.indexOf(url) === idx)
		.filter((url) => /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url));

	if (!urls.length) {
		return content;
	}

	const requests = urls.map((url) =>
		apiFetch<{
			sourceURL: string;
			url: string;
		}>({
			path: "/magazine-blocks/v1/image-import",
			method: "POST",
			data: {
				url: url,
			},
		})
	);

	const response = await Promise.allSettled(requests);

	for (const res of response) {
		if ("fulfilled" === res.status) {
			const { sourceURL, url } = res.value;
			if (url) {
				content = content.replaceAll(sourceURL, url);
			}
		}
	}

	return content;
};

// Define the template interface
interface Template {
	id: number;
	ID: number;
	post_title?: string;
	post_thumbnail?: string;
	previewUrl: string;
	category?: { name: string; slug: string }[];
	tags?: string[];
	[key: string]: any;
}

interface TemplatesProps {
	onSelectTab: (tab: string) => void;
}

export const Templates: React.FC<TemplatesProps> = () => {
	const { query, type } = useSiteBuilder();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const [searchParams, setSearchParams] = useSearchParams();
	const statusParam = searchParams.get("status");
	const status = statusParam ? [statusParam] : ["publish", "draft"];
	const page = searchParams.get("page") || undefined;
	const search = searchParams.get("search") || undefined;

	const navigate = useNavigate();

	const queryParams = {
		search,
		page,
		status,
	};

	const customQuery = useQuery({
		queryKey: ["templates", queryParams, type],
		queryFn: async () => {
			const response = await apiFetch<Response>({
				path:
					"magazine-blocks/v1/builder-templates?" +
					Object.entries(queryParams)
						.reduce((acc, [key, value]) => {
							if (Array.isArray(value)) {
								value.forEach((v) => acc.push(`${key}[]=${v}`));
							} else if (value) {
								acc.push(`${key}=${value}`);
							}
							return acc;
						}, [] as string[])
						.join("&"),
				parse: false,
			});
			if (!(response.status >= 200 && response.status < 300))
				throw response;
			let data = (await response.json()) as Template[];

			// Filter by current template type if not "all"
			if (type && type !== "all") {
				data = data.filter((template) => {
					return (
						template.meta?._mzb_template === type ||
						template.template_type === type ||
						template.type === type
					);
				});
			}

			return data;
		},
	});

	const formatTemplateType = (type: string): string => {
		return type
			.split("-")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	const createMutation = useMutation({
		mutationFn: () => {
			return apiFetch<Object>({
				path: "magazine-blocks/v1/builder-templates",
				method: "POST",
				data: {
					title: formatTemplateType(type as string) + " Template",
					status: "draft",
					meta: {
						_mzb_template: type,
					},
				},
			});
		},
		onSuccess: (data) => {
			if ("id" in data) {
				window.location.href =
					localized.adminUrl + `post.php?post=${data.id}&action=edit`;
			}
		},
		onError: (error: any) => {
			if (error instanceof Error) {
				toast({
					title: error.message,
					status: "error",
					isClosable: true,
				});
			}
		},
	});

	const { mutate, isLoading } = useMutation(
		async (template: Template) => {
			try {
				const response = await axios.get(
					`https://blocks.themegrilldemos.com/wp-json/blockart-library/v1/mzb/content?id=${
						template.id || template.ID
					}`
				);
				const content = await saveImagesLocally(response?.data ?? "");

				const newPost = await apiFetch<Object>({
					path: "magazine-blocks/v1/builder-templates",
					method: "POST",
					data: {
						title: formatTemplateType(type as string) + " Template",
						status: "draft",
						content: content,
						meta: {
							_mzb_template: type,
						},
					},
				});

				return newPost;
			} catch {
				throw Error("Failed to import");
			}
		},
		{
			onSuccess: (data) => {
				if ("id" in data) {
					window.location.href =
						localized.adminUrl +
						`post.php?post=${data.id}&action=edit`;
				}
			},
		}
	);

	if (query.isLoading) {
		return (
			<Center h="200px">
				<VStack spacing={4}>
					<Spinner size="xl" />
					<Text>
						{sprintf(
							__("Loading %s templates...", "magazine-blocks"),
							type
						)}
					</Text>
				</VStack>
			</Center>
		);
	}

	if (query.isError) {
		return (
			<Center h="200px">
				<Text color="red.500">
					{query.error instanceof Error
						? query.error.message
						: __("Unknown error occurred!", "magazine-blocks")}
				</Text>
			</Center>
		);
	}

	const data = (query.data ?? []).filter((template) => {
		if ("all" === type) return true;
		if ("category" in template) {
			const cat = template.category as {
				name: string;
				slug: string;
			}[];
			return cat.some((c) => c.slug === type);
		}
		return true;
	});

	const handlePreview = (template: Template, event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		const previewUrl = `https://blocks.themegrilldemos.com/mzb-builder/${
			template?.post_name || template?.slug
		}`;

		window.open(previewUrl, "_blank");

		toast({
			title: "Preview opened",
			description: `Preview for ${template?.post_title} opened in new tab`,
			status: "info",
			duration: 3000,
			isClosable: true,
		});
	};

	const onSelectTab = (title: string) => {
		navigate(`/site-builder?type=${title.toLowerCase()}`);
	};

	const getImageDimensions = (type: string) => {
		if (type === "header") {
			return { width: 750, height: 100 };
		} else if (type === "footer") {
			return { width: 750, height: 370 };
		} else if (type === "front") {
			return { width: 810, height: 980 };
		} else if (type === "single") {
			return { width: 810, height: 980 };
		} else if (type === "archive") {
			return { width: 810, height: 980 };
		} else if (type === "404") {
			return { width: 810, height: 980 };
		} else if (type === "search") {
			return { width: 810, height: 980 };
		} else if (type === "page") {
			return { width: 810, height: 980 };
		} else {
			return { width: 810, height: 980 };
		}
	};

	const { width, height } = getImageDimensions(type);

	// Check if we have custom templates for current type
	const hasCustomTemplates = customQuery?.data && customQuery.data.length > 0;

	return (
		<Box width="full" p={8}>
			<VStack spacing={6} align="stretch" gap="0">
				<HStack
					justify="space-between"
					style={
						type !== "all"
							? { marginBottom: "8px" }
							: { marginBottom: "26px" }
					}
					gap="14px"
				>
					<Heading fontWeight="600" fontSize="16px">
						{sprintf(
							__("%s Templates", "magazine-blocks"),
							formatTemplateType(type ?? "")
						)}
					</Heading>
					{type !== "all" && (
						<Button
							onClick={() => createMutation.mutate()}
							isLoading={createMutation.isLoading}
							display="flex"
							gap="8px"
							alignItems="center"
							justifyContent="center"
							color="#690AA0"
							borderRadius="2px"
							border="1px solid #690AA0"
							padding="8px 12px"
							fontSize="sm"
							fontWeight="medium"
							backgroundColor="transparent"
							_hover={{
								textDecoration: "none",
								bg: "#690AA0",
								borderColor: "#690AA0",
								color: "#fff",
							}}
							_focus={{
								borderColor: "none",
								boxShadow: "none",
							}}
							_active={{ bg: "purple.300" }}
							transition="background-color 0.2s ease"
							w="fit-content"
							px={4}
							py={2}
							boxShadow="sm"
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8.00002 14.6333C7.60002 14.6333 7.33336 14.3666 7.33336 13.9666V8.6333H2.06669C1.66669 8.6333 1.40002 8.36664 1.40002 7.96664C1.40002 7.56664 1.66669 7.29997 2.06669 7.29997H7.33336V2.0333C7.33336 1.6333 7.60002 1.36664 8.00002 1.36664C8.40002 1.36664 8.66669 1.6333 8.66669 2.0333V7.29997H13.9334C14.3334 7.29997 14.6 7.56664 14.6 7.96664C14.6 8.36664 14.3334 8.6333 13.9334 8.6333H8.66669V13.9666C8.66669 14.3666 8.40002 14.6333 8.00002 14.6333Z"
									fill="currentColor"
								/>
							</svg>
							{__("Create Template", "magazine-blocks")}
						</Button>
					)}
				</HStack>
				<Stack gap="14px">
					{type === "all" ? (
						hasCustomTemplates ? (
							<TemplateLists />
						) : (
							<SimpleGrid columns={3} spacing={6}>
								{templateCards.map(
									({ title, slug, component }) => (
										<TemplateCard
											key={slug}
											title={title}
											onClick={() => {
												onSelectTab(slug);
											}}
										>
											{component}
										</TemplateCard>
									)
								)}
							</SimpleGrid>
						)
					) : (
						<>
							{hasCustomTemplates && (
								<>
									<VStack align="stretch" spacing={4}>
										<TemplateLists templateType={type} />
									</VStack>
								</>
							)}
							<VStack align="stretch" spacing={4} gap="0">
								{hasCustomTemplates && (
									<Heading
										size="sm"
										paddingBottom="8px"
										fontSize="13px"
										fontWeight="600"
										fontStyle="normal"
										margin="14px 0 12px 0"
									>
										{sprintf(
											__(
												"Ready-Made %s",
												"magazine-blocks"
											),
											formatTemplateType(type ?? "")
										)}
									</Heading>
								)}
								<Grid
									templateColumns="repeat(auto-fill, minmax(250px, 1fr))"
									gap={6}
								>
									{data.map((template) => (
										<Box
											key={template.ID}
											borderRadius="sm"
											bgColor="white"
											onClick={template.onClick}
											border="1px"
											borderColor="gray.200"
											onMouseEnter={onOpen}
											onMouseLeave={onClose}
										>
											<Box
												p="12px"
												position="relative"
												width="100%"
												paddingTop={`${
													(height / width) * 100
												}%`}
												overflow="hidden"
												borderRadius="sm"
											>
												<Image
													src={
														template.post_thumbnail ||
														defaultImage
													}
													alt={template.post_title}
													position="absolute"
													top={0}
													left={0}
													width="100%"
													height="100%"
													objectFit="cover"
												/>
												<Flex
													position="absolute"
													top="0"
													left="0"
													right="0"
													zIndex="1"
													bottom="0"
													bgColor="rgba(0, 0, 0, 0.30)"
													justifyContent="center"
													alignItems="center"
													transition="opacity 0.3s"
													_groupHover={{ opacity: 1 }}
													visibility={
														isOpen ||
														createMutation.isLoading
															? "visible"
															: "hidden"
													}
													pointerEvents={
														isOpen ||
														createMutation.isLoading
															? "auto"
															: "none"
													}
													opacity={
														isOpen ||
														createMutation.isLoading
															? 1
															: 0
													}
												>
													<ButtonGroup spacing={3}>
														<Button
															fontStyle="normal"
															fontWeight="400px"
															fontSize="12px"
															borderRadius="2px"
															padding="14px 12px"
															gap="4px"
															lineHeight="16px"
															iconSpacing={0}
															color="purple"
															bgColor="#fff"
															variant="solid"
															onClick={(e) =>
																handlePreview(
																	template as unknown as Template,
																	e
																)
															}
															size="xs"
															leftIcon={
																<svg
																	width="14"
																	height="14"
																	viewBox="0 0 13 13"
																	fill="none"
																	xmlns="http://www.w3.org/2000/svg"
																>
																	<path
																		fillRule="evenodd"
																		clipRule="evenodd"
																		//eslint-disable-next-line max-len
																		d="M2.58852 6.65723C3.69519 8.37776 5.00057 9.15723 6.5 9.15723C7.99943 9.15723 9.30481 8.37776 10.4115 6.65723C9.30481 4.93669 7.99943 4.15723 6.5 4.15723C5.00057 4.15723 3.69519 4.93669 2.58852 6.65723ZM1.57125 6.39998C2.8311 4.30024 4.47222 3.15723 6.5 3.15723C8.52778 3.15723 10.1689 4.30024 11.4287 6.39998C11.5238 6.55832 11.5238 6.75613 11.4287 6.91447C10.1689 9.01421 8.52778 10.1572 6.5 10.1572C4.47222 10.1572 2.8311 9.01421 1.57125 6.91447C1.47625 6.75613 1.47625 6.55832 1.57125 6.39998ZM5.43934 5.59657C5.72064 5.31526 6.10217 5.15723 6.5 5.15723C6.89783 5.15723 7.27936 5.31526 7.56066 5.59657C7.84196 5.87787 8 6.2594 8 6.65723C8 7.05505 7.84196 7.43658 7.56066 7.71789C7.27936 7.99919 6.89783 8.15723 6.5 8.15723C6.10217 8.15723 5.72064 7.99919 5.43934 7.71789C5.15804 7.43658 5 7.05505 5 6.65723C5 6.2594 5.15804 5.87787 5.43934 5.59657ZM6.5 6.15723C6.36739 6.15723 6.24022 6.2099 6.14645 6.30367C6.05268 6.39744 6 6.52462 6 6.65723C6 6.78983 6.05268 6.91701 6.14645 7.01078C6.24022 7.10455 6.36739 7.15723 6.5 7.15723C6.63261 7.15723 6.75978 7.10455 6.85355 7.01078C6.94732 6.91701 7 6.78983 7 6.65723C7 6.52462 6.94732 6.39744 6.85355 6.30367C6.75978 6.2099 6.63261 6.15723 6.5 6.15723Z"
																		fill="#690AA0"
																	/>
																</svg>
															}
														>
															{__(
																"Preview",
																"magazine-blocks"
															)}
														</Button>
														<Button
															isLoading={
																isLoading
															}
															fontStyle="normal"
															fontWeight="400px"
															fontSize="12px"
															padding="14px 12px"
															borderRadius="2px"
															iconSpacing={0}
															gap="4px"
															lineHeight="16px"
															colorScheme="purple"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																mutate(
																	template as Template
																);
															}}
															size="xs"
															leftIcon={
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	width="15"
																	height="15"
																	viewBox="0 0 15 15"
																	fill="none"
																>
																	<path
																		// eslint-disable-next-line max-len
																		d="M11.6096 11.1684C11.4783 11.1691 11.3505 11.1258 11.2466 11.0455C11.1427 10.9652 11.0686 10.8525 11.0361 10.7253C11.0036 10.598 11.0146 10.4636 11.0673 10.3433C11.12 10.223 11.2114 10.1238 11.327 10.0614C11.7495 9.82988 12.0629 9.44025 12.1985 8.97797C12.2689 8.74937 12.2922 8.50884 12.2669 8.27098C12.2416 8.03312 12.1682 7.80288 12.0513 7.59421C11.8958 7.30669 11.6653 7.0667 11.3844 6.89976C11.1034 6.73282 10.7824 6.64519 10.4555 6.64619H9.86669C9.73297 6.65042 9.6018 6.60899 9.49477 6.52872C9.38774 6.44845 9.31123 6.33412 9.27786 6.20456C9.17209 5.78861 8.98529 5.39761 8.72815 5.05398C8.47101 4.71034 8.14859 4.42083 7.77936 4.20203C7.41013 3.98323 7.00135 3.83943 6.57645 3.7789C6.15155 3.71836 5.71888 3.74226 5.30324 3.84923C4.88728 3.95499 4.49628 4.1418 4.15265 4.39894C3.80902 4.65608 3.5195 4.9785 3.3007 5.34773C3.0819 5.71696 2.93811 6.12574 2.87757 6.55064C2.81703 6.97554 2.84093 7.40821 2.94791 7.82385C3.07996 8.32159 3.32361 8.78272 3.66039 9.17228C3.76332 9.28947 3.81555 9.44271 3.80562 9.59837C3.79568 9.75403 3.72439 9.89938 3.6074 10.0025C3.49021 10.1055 3.33697 10.1577 3.18131 10.1478C3.02565 10.1378 2.8803 10.0665 2.77714 9.94954C2.31568 9.4227 1.98306 8.79577 1.80557 8.11827C1.47998 7.00209 1.60523 5.80272 2.15442 4.77789C2.70361 3.75307 3.63291 2.98457 4.74262 2.63755C5.85232 2.29052 7.05388 2.39266 8.08909 2.92202C9.1243 3.45138 9.91054 4.3657 10.2789 5.46852H10.4555C11.12 5.4702 11.7651 5.69263 12.2893 6.10086C12.8136 6.50908 13.1874 7.07996 13.3518 7.72374C13.5163 8.36752 13.4622 9.04771 13.198 9.65739C12.9338 10.2671 12.4745 10.7717 11.8923 11.0919C11.806 11.1408 11.7088 11.1671 11.6096 11.1684ZM9.91968 9.49025C9.80936 9.38058 9.66012 9.31902 9.50456 9.31902C9.34899 9.31902 9.19975 9.38058 9.08943 9.49025L8.15319 10.4442V7.5C8.15319 7.34383 8.09115 7.19406 7.98072 7.08363C7.87029 6.9732 7.72052 6.91116 7.56435 6.91116C7.40819 6.91116 7.25841 6.9732 7.14799 7.08363C7.03756 7.19406 6.97552 7.34383 6.97552 7.5V10.4442L6.05694 9.49025C6.00192 9.43568 5.93667 9.3925 5.86493 9.3632C5.79319 9.33389 5.71636 9.31904 5.63887 9.31949C5.56138 9.31904 5.48456 9.33389 5.41281 9.3632C5.34107 9.3925 5.27582 9.43568 5.2208 9.49025C5.16483 9.54421 5.1201 9.60871 5.08919 9.68005C5.05828 9.75138 5.04181 9.82813 5.04071 9.90587C5.03962 9.9836 5.05393 10.0608 5.08281 10.133C5.1117 10.2051 5.1546 10.2709 5.20902 10.3264L7.14039 12.2519C7.19465 12.3048 7.25864 12.3468 7.32882 12.3755C7.40011 12.4075 7.47737 12.4241 7.55552 12.4241C7.63367 12.4241 7.71093 12.4075 7.78222 12.3755C7.85241 12.3468 7.91639 12.3048 7.97065 12.2519L9.91968 10.3264C9.97487 10.2717 10.0187 10.2065 10.0486 10.1348C10.0785 10.063 10.0939 9.98605 10.0939 9.90832C10.0939 9.83059 10.0785 9.75362 10.0486 9.68187C10.0187 9.61011 9.97487 9.54499 9.91968 9.49025Z"
																		fill="white"
																	/>
																</svg>
															}
														>
															{__(
																"Import",
																"blockart"
															)}
														</Button>
													</ButtonGroup>
												</Flex>
											</Box>
											<HStack
												justify="space-between"
												p="12px"
											>
												<Text
													as="span"
													fontWeight="500"
												>
													{template.post_title}
												</Text>
											</HStack>
										</Box>
									))}
								</Grid>
							</VStack>
						</>
					)}
				</Stack>
			</VStack>
		</Box>
	);
};
